import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { shortOrderId } from '../../utils/paginate';
import { translateKnown } from '../../i18n/knownMessages';


// Review of one fulfilled supplier order. The server decides eligibility and duplicates; this only collects the input
export default function ReviewModal({ orderId, supplierId, supplierName, onClose, onSubmitted }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const nameMissing = !anonymous && !firstName.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!rating || saving || nameMissing) return;
    setSaving(true);
    setError('');
    try {
      await api.post(`/api/suppliers/${supplierId}/reviews`, { orderId, rating, comment, anonymous, firstName: anonymous ? '' : firstName.trim() });
      onSubmitted(orderId);
    } catch (err) {
      // 409: the server already has a review for this order, so it is shown as reviewed instead of offering the action again
      if (err.response?.status === 409) return onSubmitted(orderId);
      setError(err.response?.data?.message || 'Could not save your review. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="review-modal-title" onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-lg w-full max-w-md p-5 space-y-4">
        <h2 id="review-modal-title" className="text-lg font-semibold text-gray-800">{t('dialogs.review.title')}</h2>
        <p className="text-sm text-gray-600 break-words">{t('dialogs.review.forOrder', { name: supplierName, id: shortOrderId(orderId) })}</p>
        <fieldset>
          <legend className="text-sm font-medium text-gray-700 mb-1">{t('dialogs.review.rating')}</legend>
          <div role="radiogroup" aria-label={t('dialogs.review.rating')} className="flex gap-1">
            {[0, 1, 2, 3, 4].map((label, i) => (
              <label key={label} className={`text-3xl leading-none cursor-pointer rounded px-0.5 focus-within:ring-2 focus-within:ring-blue-500 ${i < rating ? 'text-amber-500' : 'text-gray-300'}`}>
                <input type="radio" name="rating" value={i + 1} checked={rating === i + 1} onChange={() => setRating(i + 1)} aria-label={t('dialogs.review.star', { count: i + 1 })} className="sr-only" />
                ★
              </label>
            ))}
          </div>
        </fieldset>
        <label className="block text-sm font-medium text-gray-700">
          {t('dialogs.review.comment')}
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} rows={3} className="mt-1 w-full border rounded px-2 py-1 font-normal" />
          <span className="block text-xs text-gray-500 text-right">{comment.length}/500</span>
        </label>
        <fieldset className="space-y-2 text-sm text-gray-700">
          <legend className="font-medium mb-1">{t('dialogs.review.identity')}</legend>
          <label className="flex items-center gap-2"><input type="radio" name="identity" checked={!anonymous} onChange={() => setAnonymous(false)} />{t('dialogs.review.showName')}</label>
          {!anonymous && (
            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={30} placeholder={t('dialogs.review.firstNamePlaceholder')} aria-label={t('dialogs.review.firstName')} className="ml-6 w-[calc(100%-1.5rem)] border rounded px-2 py-1" />
          )}
          <label className="flex items-center gap-2"><input type="radio" name="identity" checked={anonymous} onChange={() => setAnonymous(true)} />{t('dialogs.review.postAnonymously')}</label>
        </fieldset>
        {error && <p role="alert" className="text-red-600 text-sm">{translateKnown(t, error)}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-400 text-gray-700 rounded">{t('profile.cancel')}</button>
          <button type="submit" disabled={!rating || saving || nameMissing} className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50">
            {saving ? t('dialogs.review.submitting') : t('dialogs.review.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
